import { useEffect, useState } from "react";

import { formatDateTime, formatRelative, isoInstant, type Instant } from "@/lib/dates";

// ONE timer for every `<RelativeTime>` on screen. A table of fifty rows would
// otherwise start fifty intervals; instead the first one mounted starts the
// timer and the last one unmounted stops it.
const TICK_MS = 30_000;
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | undefined;

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) {
    timer = setInterval(() => listeners.forEach((notify) => notify()), TICK_MS);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) clearInterval(timer);
  };
}

/** The clock, re-read every 30 s so "2 minutes ago" keeps moving on a page left open. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => subscribe(() => setNow(Date.now())), []);
  return now;
}

/**
 * "5 minutes ago", "in 3 hours": how fresh an instant is, at a glance. The exact
 * time (UTC, to the second) is one hover away in the tooltip and in the `datetime`
 * of the `<time>` element, so the relative wording never costs the reader the
 * precise answer. An absent or invalid instant is an em dash.
 */
export function RelativeTime({
  epochMs,
  deadline = false,
}: {
  epochMs: Instant;
  /**
   * Cette date est une ÉCHÉANCE : une fois dépassée, elle vire au rouge.
   *
   * Sans ce drapeau la couleur est héritée du parent, parce que « passé » ne
   * veut pas dire « problème ». Le composant colorait auparavant toute date
   * antérieure à maintenant, ce qui affichait « écrit il y a 1 seconde » en
   * rouge juste après une propagation réussie — un signal d'alerte sur un
   * succès. Seul l'appelant sait si la date qu'il montre est un délai qui
   * court ou un horodatage constaté.
   */
  deadline?: boolean;
}) {
  const now = useNow();
  const iso = isoInstant(epochMs);
  if (!iso) return <span>{formatRelative(epochMs, now)}</span>;

  const overdue = deadline && Date.parse(iso) < now;
  return (
    <time
      dateTime={iso}
      title={`${formatDateTime(epochMs)} UTC`}
      className={overdue ? "text-danger-fg" : undefined}
    >
      {formatRelative(epochMs, now)}
    </time>
  );
}
